import { render, html } from "lit";
import { ref } from 'lit/directives/ref.js';
import { dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RowSource, renderers } from "@omegagrid/grid";
import rp, { CodeEditor, GridEditorAdapter, GridEditorOptions, RowData } from "@reporting/rp-app-host";
import { AccountingGroup } from "@reporting/rp-types/core";
import { SsoOidcParamCodeEditorAdapter, SsoSamlParamCodeEditorAdapter } from "../adapters";
import { url } from "../utils";
import { SsoOidcPreset } from "../types";

type DataSource = {
	/** The text displayed in the cell */
	text: CellSource | string;
	/** The value of the cell */
	value?: CellSource;
	/** The ID of the cell (hidden)*/
	id?: string;
	/** The type of the data, e.g., 'AccountingGroup' (hidden) */
	type?: string;
	/** Which protocol a "Parameters" row belongs to (hidden); SAML2 when missing */
	proto?: 'saml' | 'oidc';
}

export class SsoSettingsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];
	private group: AccountingGroup;
	private presets: SsoOidcPreset[] = [];
	//private ssoSamlParams: SsoSamlParams;

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'text',
			t: 't',
			e: false,
			levelButtons: true,
			levelIndent: true,
			w: 260,
		}, {
			data: 'value',
			t: 't',
			s: { alignment: { horizontal: 'left' } },
			w: 800,
		}, {
			data: 'id',
			t: 't',
			e: false,
			hidden: true,
		}, {
			data: 'type',
			t: 't',
			e: false,
			hidden: true,
		}, {
			data: 'proto',
			t: 't',
			e: false,
			hidden: true,
		},];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		this.group = await rp.ms('accounting').get<AccountingGroup>('groups/mine');
		this.presets = await rp.ms('accounting').get<SsoOidcPreset[]>('ssoOidcPresets').catch((): SsoOidcPreset[] => []) ?? [];
		const preset = this.presets.find(p => p.id === this.group.sso_oidc_preset);
		const presetList = [
			{ key: '', value: msg('None - own application') },
			...this.presets.map(p => ({ key: p.id, value: p.name })),
		];
		const consentUrl = preset ? url.getEntraAdminConsentUrl(this.group.sso_oidc_issuer, preset.client_id) : null;
		//this.ssoSamlParams = await rp.ms('accounting').get<SsoSamlParams>(`ssoSamlParams`);
		this._rows = [
			{ l: 0, data: { text: { cs: 2, v: msg('General') } } as DataSource },
				{ l: 1, data: {
					text: msg('Session expiration'),
					value: { t: 't', v: this.group.sso_session_expiration },
					id: 'sso_session_expiration',
					type: 'AccountingGroup',
				} as DataSource },

			{ l: 0, collapsed: !this.group.sso_saml2, data: { text: { cs: 2, v: msg('SAML2 SSO') } } as DataSource },
				{ l: 1, data: {
					text: msg('Enabled'),
					value: { t: 'b', v: this.group.sso_saml2},
					id: 'sso_saml2',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('IdP metadata URL'),
					value: { t: 't', v: this.group.sso_saml2_idp_metadata_url},
					id: 'sso_saml2_idp_metadata_url',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('SP metadata URL'),
					value: { e: false, t: 't', v: url.getFederationMetadataUrl(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Login URL'),
					value: { e: false, t: 't', v: url.getSamlLoginUrl(this.group) },
				} as DataSource },

				{ l: 1, data: { text: { cs: 2, v: msg('Parameters') } } as DataSource },
					{ l: 2, data: {
						text: msg('Identity provider'),
						value: { t: 't', e: false, renderer: this.paramRenderer }, // TODO: show certificate expiration
						id: 'idp',
					} as DataSource },
					{ l: 2, data: {
						text: msg('Service provider'),
						value: { t: 't', e: false, renderer: this.paramRenderer }, // TODO: show certificate expiration
						id: 'sp',
					} as DataSource },
					{ l: 2, data: {
						text: msg('Security settings'),
						value: { t: 't', e: false, renderer: this.paramRenderer },
						id: 'security',
					} as DataSource },
					{ l: 2, data: {
						text: msg('Transformation'),
						value: { t: 't', e: false, renderer: this.paramRenderer },
						id: 'transform',
					} as DataSource },

			{ l: 0, collapsed: !this.group.sso_oidc, data: { text: { cs: 2, v: msg('OpenID Connect') } } as DataSource },
				{ l: 1, data: {
					text: msg('Enabled'),
					value: { t: 'b', v: this.group.sso_oidc },
					id: 'sso_oidc',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Issuer'),
					value: { t: 't', v: this.group.sso_oidc_issuer },
					id: 'sso_oidc_issuer',
					type: 'AccountingGroup',
				} as DataSource },
				// a preset brings our own multi-tenant application: client id, secret and default settings
				// come from it, the group only names its tenant in the issuer and consents in Entra. Its
				// redirect URI is on our deployment, so a group on a custom domain cannot use one.
				...(this.group.custom_domain ? [] : [
					{ l: 1, data: {
						text: msg('Preset'),
						value: { t: 's', v: preset?.id ?? '', dv: preset?.name ?? presetList[0].value, content: presetList },
						id: 'sso_oidc_preset',
						type: 'AccountingGroup',
					} as DataSource },
				]),
				...(preset ? [
					{ l: 1, data: {
						text: msg('Admin consent URL'),
						value: { e: false, t: 't', v: consentUrl ?? msg('Fill in the issuer with your tenant ID first') },
					} as DataSource },
				] : []),
				{ l: 1, data: {
					text: msg('Client ID'),
					value: preset
						? { e: false, t: 't', v: `${preset.client_id} (${preset.name})` }
						: { t: 't', v: this.group.sso_oidc_client_id },
					...(preset ? {} : { id: 'sso_oidc_client_id', type: 'AccountingGroup' }),
				} as DataSource },
				{ l: 1, data: {
					text: msg('Login URL'),
					value: { e: false, t: 't', v: url.getOidcLoginUrl(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Redirect URI'),
					value: { e: false, t: 't', v: preset ? url.getOidcPresetCallbackUrl(preset) : url.getOidcCallbackUrl(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Post-logout redirect URI'),
					value: { e: false, t: 't', v: url.getPostLogoutRedirectUri(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Back-channel logout URI'),
					value: { e: false, t: 't', v: url.getOidcBackchannelLogoutUrl(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Front-channel logout URI'),
					value: { e: false, t: 't', v: url.getLogoutUrl(this.group) },
				} as DataSource },

				{ l: 1, data: { text: { cs: 2, v: msg('Parameters') } } as DataSource },
					// the secret comes with the preset, the group's own would be ignored
					...(preset ? [] : [
						{ l: 2, data: {
							text: msg('Client secret'),
							value: { t: 't', e: false, renderer: this.paramRenderer },
							id: 'client_secret',
							proto: 'oidc',
						} as DataSource },
					]),
					{ l: 2, data: {
						text: msg('Settings'),
						value: { t: 't', e: false, renderer: this.paramRenderer },
						id: 'settings',
						proto: 'oidc',
					} as DataSource },

			// SCIM has no switch of its own: the endpoints exist for every group and the tenant comes
			// from the accounting the token is bound to - nothing here is saved (no id / type)
			{ l: 0, collapsed: true, data: { text: { cs: 2, v: msg('SCIM provisioning') } } as DataSource },
				{ l: 1, data: {
					text: msg('Tenant URL'),
					value: { e: false, t: 't', v: url.getScimBaseUrl(this.group) },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Users endpoint'),
					value: { e: false, t: 't', v: `${url.getScimBaseUrl(this.group)}/Users` },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Groups endpoint'),
					value: { e: false, t: 't', v: `${url.getScimBaseUrl(this.group)}/Groups` },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Service provider configuration'),
					value: { e: false, t: 't', v: `${url.getScimBaseUrl(this.group)}/ServiceProviderConfig` },
				} as DataSource },
				{ l: 1, data: {
					text: msg('Authentication'),
					value: { e: false, t: 't', v: msg('Bearer token with the admin scope'), renderer: this.scimTokenRenderer },
				} as DataSource },

			{ l: 0, collapsed: !this.group.sso, data: { text: { cs: 2, v: msg('Hash-based SSO') } } as DataSource },
				{ l: 1, data: {
					text: msg('Enabled'),
					value: { t: 'b', v: this.group.sso},
					id: 'sso',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('SSO key'),
					value: { e: false, t: 't', renderer: this.ssoKeyRenderer },
				} as DataSource },
				{ l: 1, data: {
					text: msg('SSO endpoint'),
					value: { e: false, t: 't', v: url.getSSOUrl(this.group) },
				} as DataSource },
		];
		return this._rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			refreshAfterSave: true,
			rowTree: false,
			editable: rp.userProvider.isGroupAdmin(),
			saveMode: 'edited_rows',
			toolbar: {
				new: { enabled: false },
				save: { enabled: rp.userProvider.isGroupAdmin() },
				discard: { enabled: rp.userProvider.isGroupAdmin() },
			},
		});
	}

	async save(rowData: RowData[]) {
		const data: AccountingGroup = {};
		const gridData = rowData.map(row => (row.data as DataSource));
		for (const d of gridData) {
			if (d.id && d.type === 'AccountingGroup') {
				(data as any)[d.id] = d.value;
			}
		}
		// the "own application" choice is an empty key
		if ('sso_oidc_preset' in data && !(data as any).sso_oidc_preset) data.sso_oidc_preset = null;
		data.id_group = this.group.id_group;
		await rp.ms('accounting').post('groups', data);
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'paramEditBtn':
				const component = dom.createElement<CodeEditor>('rp-editor-code');
				if (cell.row.data.proto === 'oidc') {
					component.adapter = new SsoOidcParamCodeEditorAdapter(this.group.id_group, cell.row.data.id);
					rp.host.open('ssooidc-param-editor', {
						component: component,
						title: `OpenID Connect - ${msg(cell.row.data.text)}`,
					});
				} else {
					component.adapter = new SsoSamlParamCodeEditorAdapter(this.group.id_group, cell.row.data.id);
					rp.host.open('ssosaml-param-editor', {
						component: component,
						title: `SAML2 - ${msg(cell.row.data.text)}`,
					});
				}
				break;
		}
	}

	paramRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(html`
				<og-button
					icon="pen"
					color="gray"
					.disabled="${!rp.userProvider.isGroupAdmin()}"
					style="height: 18px; width: 18px"
					title="${msg('Edit')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('paramEditBtn', cell)}">
				</og-button>
				<div style="flex: 1;">
					<span>${cell.displayValue || cell.value}</span>
				</div>
			`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	ssoKeyRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		let ssoKeySpan: HTMLSpanElement;

		render(html`
				<div style="flex: 1;">
                	<span ${ref((el) => ssoKeySpan = el as HTMLSpanElement)}>${this.group.sso ? '*'.repeat(32) : ''}</span>
            	</div>
				${this.group.sso ? html`
				<og-button
					icon="eye"
					style="height: 18px; width: 18px"
					title="${msg('Show key')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.showSsoKey(ssoKeySpan, cell)}">
				</og-button>
				` : ''}
			`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	/** The SCIM authentication row: the hint plus a button that issues a provisioning token right away */
	scimTokenRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(html`
				<div style="flex: 1;">
					<span>${cell.displayValue || cell.value}</span>
				</div>
				<og-button
					icon="key"
					style="height: 18px; width: 18px"
					.disabled="${!rp.userProvider.isGroupAdmin()}"
					title="${msg('Generate access token')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.generateScimToken()}">
				</og-button>
			`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	/**
	 * Issues a SCIM provisioning token the way the Access Tokens form does: a named token for the
	 * current admin with the admin scope, bound to the current company, without expiration
	 * (provisioning runs for months). It shows up as "SCIM" in Access Tokens, where it can be revoked.
	 *
	 * TODO: issue the token to an application identity instead of the admin who clicks. A token
	 * delegated to a person dies with that person's account (TokenRevocation), and the audit trail
	 * names a human for what the identity provider did. The pieces exist: a per-group system
	 * application "SCIM" in P_APPLICATION (an accounting of the group, SCOPE = admin,
	 * REQUIRES_USER = 0 - one global client id is not possible, issueForApplication refuses
	 * accounting null / -1 and the SCIM tenant is derived from the token's accounting),
	 * issueToken({ client_id, username: null, ... }) already mints a token for the application
	 * itself, and ScimRoute::tenant() already treats such a token as the group admin. Missing: a
	 * find-or-create endpoint for that system application (rp-ms-accounting ApplicationRoute) and
	 * ideally a SYSTEM flag on P_APPLICATION so the Applications grid protects it (deleting the
	 * application revokes its tokens, which doubles as rotation).
	 */
	async generateScimToken() {
		const command = await rp.host.openMenu({
			content: 'yes_no',
			text: `${msg('Generate a SCIM provisioning token?')} ${msg('It is issued to your account with the admin scope and never expires; you can revoke it in Access Tokens.')}`,
		});
		if (command?.id != 'Y') return;

		const tokenInfo = await rp.auth.client.issueToken({
			name: 'SCIM',
			username: rp.userProvider.id,
			accounting: rp.accountingProvider.a,
			scope: ['admin'],
			expiration: 0,
		});

		if (!tokenInfo || (tokenInfo as any).error) {
			rp.host.showAlert({
				type: 'danger',
				title: msg('SCIM provisioning'),
				content: `${msg('Failed to create token')}: ${(tokenInfo as any)?.error_description || (tokenInfo as any)?.error || ''}`,
			});
			return;
		}

		this.showToken(tokenInfo.access_token);
	}

	/** Shows a freshly issued token once, with a copy button (mirrors the Access Tokens form) */
	showToken(token: string) {
		const div = dom.createElement<HTMLDivElement>('div', { style: {
			margin: '15px',
			userSelect: 'text',
			overflow: 'hidden',
		}});

		render(html`
			<div style="display: flex; flex-direction: row; align-items: stretch; width: 100%; height: 20px">
				<input
					readonly
					style="flex: 1; background: transparent; border: 1px solid var(--og-accent-color); outline: none; color: var(--og-text-color)"
					.value="${token}" />
				<og-button
					style="flex: 0 0 20px"
					icon="copy"
					title="${msg('Copy to clipboard')}"
					@click=${() => navigator.clipboard.writeText(token)}>
				</og-button>
			</div>

			<div>
				⚠️ ${msg('Please copy and store the Access Token now.')}
				${msg('For security reasons, you will not be able to view it again.')}
			</div>
			<div>
				${msg('The token is bound to your account and stops working when it is disabled; use an application token for a person-independent setup.')}
			</div>
		`, div);

		rp.host.bucket.openDialog({
			component: div,
			caption: msg('SCIM access token'),
			closable: true,
			width: 750,
			height: 250,
			resizable: true,
			location: 'center',
		});
	}

	async showSsoKey(spanElement: HTMLSpanElement, cell: CellModel) {
		const ssoKey = (await rp.ms('accounting').get<Record<string, string>>(`ssoKey`)).sso_key;
		cell.value = ssoKey;
		if (spanElement) {
			spanElement.innerHTML = ssoKey;
		}
	}

}
